// Shapes of the web role's API (pr/directory, pr/overview). Money is integer cents, points integers,
// rates decimal strings.

export type PartnerStatus = 'earning' | 'claim_pending' | 'not_linked'

export type Tier = { condition: string; rate: string | null }

export type DirectoryCard = {
  id: string
  slug: string
  name: string
  website: string | null
  description: string | null
  logo_url: string | null
  stack_tags: string[]
  layers: string[]
  credit_mode: 'none' | 'manual'
  limited: boolean
  rate: { kind: 'starting' | 'yours'; rate: string | null }
  tier_text: string | null
  tiers: Tier[]
  next_step: string | null
  reward_forms: ('cash' | 'credit')[]
  key_terms: string[]
  status: PartnerStatus | null
  cash_available_cents: number | null
}

export type DirectoryList = {
  rows: DirectoryCard[]
  next_cursor: string | null
  signed_in: boolean
  layers: Record<string, string>
}

export type RateCard = {
  policy_version_id?: string | null
  spend_month?: string | null
  paid_streak?: number | null
  tenure_rate?: string | null
  spend_rate?: string | null
  promo_rate?: string | null
  rate?: string | null
  next_step_text?: string | null
  cap_remaining?: number | null
  data_complete_through?: string | null
  reasons?: string[]
}

export type DirectoryEntry = DirectoryCard & {
  rate_card: RateCard | null
  you: {
    status: PartnerStatus
    reported_spend_cents: number
    rebated_cents: number
    cash_available_cents: number
    points_awaiting_funding: number
    points_earned: number | null
    would_have_earned?: number | null
  } | null
}

export type Overview = {
  company: { id: string; name: string; role: 'admin' | 'member' | null }
  can_redeem: boolean
  balance: { pending: number; spendable: number; reserved: number; ready: number; negative: boolean }
  ready: { points: number; cents: number }
  debt_points: number
  cash_value_cents_per_point: number
  awaiting: { partner_id: string; name: string; slug: string | null; points: number; cents: number }[]
  awaiting_total_points: number
  payouts_in_progress: {
    id: string
    partner_id: string
    partner_name: string
    method: string
    points: number
    amount_cents: number
    status: string
    created_at: string
  }[]
  partners: {
    partner_id: string
    name: string
    slug: string | null
    status: PartnerStatus
    rate: string | null
    points_awaiting_funding: number
  }[]
  would_have_earned?: { partner_id: string; name: string; slug: string | null; points: number }[]
  found: { claim_id: string; partner_id: string; partner_name: string; account_name: string }[]
  activity: {
    rows: { journal_id: string; kind: string; at: string; points_delta: number; points_moved?: number; memo: string | null; ref_type: string; ref_id: string }[]
    next_cursor: string | null
  }
}

export const STATUS_LABEL: Record<PartnerStatus, string> = {
  earning: 'Earning',
  claim_pending: 'Checking',
  not_linked: 'Not linked',
}

export const REASON_LABEL: Record<string, string> = {
  history_missing: 'Some past months are missing, so months in a row may show low',
  before_platform_join: 'Earlier spend does not count',
  monthly_cap_reached: 'You reached the most points you can earn here this month',
  awaiting_partner_payment: 'Points are on the way',
}
